import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './ClassesSection.css';
import ClassDetails from '../schedule/ClassDetails';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';

// Fetch Requests:
// 1. GET /instructors - Fetches all instructors
// 2. GET /classes - Fetches all classes
// 3. GET /classes/{classId}/images - Fetches images for a specific class
// 4. GET /classes/{classId} - Fetches details for a specific class
// 5. GET /classes/instructor/{instructorId} - Fetches classes for a specific instructor


const ClassesSection = () => {
  const { t, i18n } = useTranslation();
  const { currentTheme, themes } = useTheme();
  const theme = themes[currentTheme];
  
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [instructorClasses, setInstructorClasses] = useState([]);
  const [uniqueClasses, setUniqueClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showClassDetails, setShowClassDetails] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const classesListRef = useRef(null); // Reference to the classes list container

  // Log current language
  useEffect(() => {
    // console.log('Current language:', i18n.language);
  }, [i18n.language]);

   // Scroll to top when component mounts
   useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add a useEffect to check if the popup-root element exists
  useEffect(() => {
    const popupRoot = document.getElementById('popup-root');
    // console.log('Popup root element exists:', !!popupRoot);
    
    if (!popupRoot) {
      // console.warn('popup-root element is missing! Creating one...');
      const newPopupRoot = document.createElement('div');
      newPopupRoot.id = 'popup-root';
      document.body.appendChild(newPopupRoot);
    }
  }, []);

  // Use state to store fetched class images
  const [classImages, setClassImages] = useState({});
  
  // Function to fetch images for a specific class
  const fetchClassImages = async (classId) => {
    if (classImages[classId]) return; // Already fetched
    
    try {
      // console.log(`Fetching images for class ${classId}`);
      const response = await axios.get(`classes/${classId}/images`);
      
      if (response.data && response.data.images && Array.isArray(response.data.images) && response.data.images.length > 0) {
        // console.log(`Got ${response.data.images.length} images for class ${classId}:`, response.data.images);
        setClassImages(prev => ({
          ...prev,
          [classId]: response.data.images
        }));
      } else {
        // console.log(`No images found for class ${classId}`);
        
        // Try to find the class in uniqueClasses to use its processed images as a fallback
        const classData = uniqueClasses.find(c => c.id === classId);
        if (classData && classData.processedImages && classData.processedImages.length > 0) {
          // console.log(`Using processed images as fallback for class ${classId}:`, classData.processedImages);
          setClassImages(prev => ({
            ...prev,
            [classId]: classData.processedImages
          }));
        }
      }
    } catch (err) {
      // console.error(`Error fetching images for class ${classId}:`, err);
      
      // Try to use processed images as fallback on error
      const classData = uniqueClasses.find(c => c.id === classId);
      if (classData && classData.processedImages && classData.processedImages.length > 0) {
        // console.log(`Using processed images as fallback after error for class ${classId}:`, classData.processedImages);
        setClassImages(prev => ({
          ...prev,
          [classId]: classData.processedImages
        }));
      }
    }
  };

  // Fetch all active instructors
  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        setLoading(true);
        const response = await axios.get('classes/instructors');
        setInstructors(response.data || []);
        
        // Auto-select the first instructor if available
        if (response.data && response.data.length > 0) {
          setSelectedInstructor(response.data[0]);
          fetchInstructorClasses(response.data[0].id);
        } else {
          setLoading(false);
        }
      } catch (err) {
        // console.error('Error fetching instructors:', err);
        setError(t('error') + ' ' + t('failed_load_instructors'));
        setLoading(false);
      }
    };

    fetchInstructors();
  }, []);

  // Fetch classes for a specific instructor
  const fetchInstructorClasses = async (instructorId) => {
    try {
      setLoading(true);
      const response = await axios.get(`classes/instructor/${instructorId}`);
      
      // Get all future classes - filter out past classes
      const now = new Date();
      const allFutureClasses = (response.data || []).filter(cls => {
        // Check if the class has a date and time
        if (cls.start_time && cls.start_time.includes('T')) {
          const classDate = new Date(cls.start_time);
          return classDate >= now;
        }
        // If no proper date format, include it by default
        return true;
      });
      
      // Helper function to normalize class names for consistent comparison
      const normalizeClassName = (name) => {
        if (!name) return '';
        // Convert to lowercase, trim whitespace, and remove special characters
        return name.toLowerCase().trim();
      };
      
      // Group classes by normalized name
      const classesByName = {};
      allFutureClasses.forEach(cls => {
        const rawClassName = cls.class_name || cls.name || cls.title || t('unnamed_class');
        const normalizedName = normalizeClassName(rawClassName);
        
        if (!classesByName[normalizedName]) {
          classesByName[normalizedName] = [];
        }
        classesByName[normalizedName].push(cls);
      });
      
      // Create a list of unique classes (one per name)
      // If multiple classes have the same name but different difficulties, use "various"/"rôzne"
      const uniqueClassesArray = Object.entries(classesByName).map(([normalizedName, classes]) => {
        // Take the first class as the representative
        const representativeClass = { ...classes[0] }; // Create a copy to avoid modifying the original
        
        // Check if there are multiple difficulty levels for this class name
        if (classes.length > 1) {
          const difficulties = new Set(classes.map(c => c.difficulty).filter(Boolean));
          
          if (difficulties.size > 1) {
            // Multiple difficulty levels - set to "various"/"rôzne"
            representativeClass.difficulty = 'various';
          }
        }
        
        return representativeClass;
      });
      
      // Process class data with field mapping awareness
      const processedClasses = uniqueClassesArray.map(cls => {
        // Extract the class name from the appropriate field
        const className = cls.class_name || cls.name || cls.title || t('unnamed_class');
        
        // Process image URL - Handle JSON array of image URLs
        let processedImages = [];
        if (cls.image_url) {
          try {
            // First check if it's already a parsed array (from server)
            if (Array.isArray(cls.image_url) && cls.image_url.length > 0) {
              processedImages = cls.image_url;
            } 
            // Otherwise try to parse it as a JSON string
            else if (typeof cls.image_url === 'string') {
              // Handle both JSON array strings and direct URL strings
              if (cls.image_url.trim().startsWith('[')) {
                const parsed = JSON.parse(cls.image_url);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  processedImages = parsed;
                }
              } else if (cls.image_url.includes('http')) {
                // It's a direct URL, not a JSON string
                processedImages = [cls.image_url];
              }
            }
          } catch (e) {
            // Fallback - if parsing fails but it looks like a URL, use it directly
            if (typeof cls.image_url === 'string' && cls.image_url.includes('http')) {
              processedImages = [cls.image_url];
            }
          }
        }
        
        // Format dates from API
        let formattedDate = cls.date;
        let formattedTime = cls.start_time;
        
        // Try to format date from start_time if it's a full timestamp
        if (cls.start_time && cls.start_time.includes('T')) {
          try {
            const startDate = new Date(cls.start_time);
            formattedDate = startDate.toLocaleDateString();
            formattedTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } catch (e) {
            // Error handling
          }
        }
        
        return {
          ...cls,
          // Ensure these fields exist with default values if missing
          title: className,
          duration: cls.duration || 60,
          max_capacity: cls.max_capacity || 20,
          price: cls.price !== undefined ? cls.price : 0,
          difficulty: cls.difficulty || 2,
          description: cls.description || '',
          date: formattedDate,
          start_time: formattedTime,
          processedImages: processedImages
        };
      });
      
      // Set both state variables with the same deduplicated data
      setInstructorClasses(processedClasses);
      setUniqueClasses(processedClasses);
      setLoading(false);
    } catch (err) {
      setError(t('error') + ' ' + t('failed_load_classes'));
      setLoading(false);
    }
  };

  // Handle instructor selection
  const handleInstructorClick = (instructor) => {
    setSelectedInstructor(instructor);
    fetchInstructorClasses(instructor.id);
    
    // Check if we're on a mobile device (based on screen width)
    // Use a slight delay to ensure the classes have time to load
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        if (classesListRef.current) {
          const yOffset = -80; // Adjust this value to account for any fixed headers
          const y = classesListRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
          
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 500); // 500ms delay to allow for data fetching and rendering
    }
  };

  // Handle closing class details
  const handleCloseClassDetails = () => {
    setShowClassDetails(false);
    setSelectedClass(null);
  };

  // Get difficulty text based on numerical value
  const getDifficultyText = (difficulty) => {
    if (difficulty === 'various') return t('various');
    if (!difficulty) return t('normal');
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return t('relax');
      case 2:
        return t('medium');
      case 3:
        return t('dynamic');
      default:
        return t('normal');
    }
  };

  // Get difficulty class
  const getDifficultyClass = (difficulty) => {
    if (difficulty === 'various') return 'yoga-difficulty-various';
    if (!difficulty) return 'yoga-difficulty-unknown';
    
    // Convert to number if it's a string
    const difficultyNum = parseInt(difficulty, 10);
    
    switch(difficultyNum) {
      case 1:
        return 'yoga-difficulty-beginner';
      case 2:
        return 'yoga-difficulty-intermediate';
      case 3:
        return 'yoga-difficulty-advanced';
      default:
        return 'yoga-difficulty-unknown';
    }
  };

  // Memoize the fetchClassImages function to avoid infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  const memoizedFetchClassImages = useCallback(
    (classId) => fetchClassImages(classId), 
    [classImages, uniqueClasses]
  );

  // Fetch images for each class when uniqueClasses changes - add proxy option for S3 images
  useEffect(() => {
    if (!uniqueClasses.length) return;
    
    // console.log('Fetching images for all classes');
    
    // Helper function to check if URL is an S3 URL
    const isS3Url = (url) => {
      return url && typeof url === 'string' && url.includes('s3.');
    };
    
    // Pre-process S3 images in uniqueClasses
    uniqueClasses.forEach(cls => {
      if (cls.processedImages && Array.isArray(cls.processedImages)) {
        cls.processedImages = cls.processedImages.map(imgUrl => {
          // Check if the image is from S3 and use a local proxy if needed
          if (isS3Url(imgUrl)) {
            // console.log(`Using original S3 URL for image: ${imgUrl}`);
            return imgUrl;
          }
          return imgUrl;
        });
      }
      
      if (cls.id) {
        memoizedFetchClassImages(cls.id);
      }
    });
  }, [uniqueClasses, memoizedFetchClassImages]);

  // Generate schema.org structured data for classes
  const generateSchemaOrgData = () => {
    if (!uniqueClasses.length || !selectedInstructor) return null;
    
    const courseData = uniqueClasses.map(cls => ({
      "@type": "Course",
      "name": cls.title,
      "description": cls.description || t('no_description'),
      "provider": {
        "@type": "Organization",
        "name": "TiDeaSolis Yoga tdio",
        "sameAs": "https://www.tideasolis.sk/"
      },
      "instructor": {
        "@type": "Person",
        "name": selectedInstructor.name,
        "jobTitle": t('yoga_instructor')
      },
      "courseCode": cls.id,
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "name": cls.title,
        "startDate": cls.date + "T" + cls.start_time,
        "location": {
          "@type": "Place",
          "name": "TiDeaSolis Yoga tdio",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Roava",
            "addressCountry": "SK"
          }
        },
        "offers": {
          "@type": "Offer",
          "price": cls.price,
          "priceCurrency": "EUR",
          "availability": cls.max_capacity > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
          "validFrom": new Date().toISOString()
        }
      }
    }));
    
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": courseData.map((course, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": course
      }))
    };
  };

  // Language and SEO-related variables
  const pageTitleSk = "Na hodiny jogy | TiDeaSolis Yoga tdio";
  const pageDescriptionSk = "Objavte rozmanit hodiny jogy, ktor ponkame v namom tdie. Kad hodina je navrhnut tak, aby poskytovala jedinezn zitok na zklade vaich potrieb a preferenci.";
  const isSlovak = i18n.language === 'sk';
  
  // Add SEO metadata for Slovak language users
  useEffect(() => {
    if (isSlovak) {
      // Set page title and meta description
      document.title = pageTitleSk;
      
      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', pageDescriptionSk);
      
      // Set language attribute on html tag
      document.documentElement.setAttribute('lang', 'sk');
      
      // Add Open Graph tags
      const updateMetaTag = (property, content) => {
        let metaTag = document.querySelector(`meta[property="${property}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', property);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', content);
      };
      
      updateMetaTag('og:locale', 'sk_SK');
      updateMetaTag('og:title', pageTitleSk);
      updateMetaTag('og:description', pageDescriptionSk);
      
      // Add hreflang links for language alternates
      const createOrUpdateLinkElement = (rel, hreflang, href) => {
        let linkElement = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
        
        if (!linkElement) {
          linkElement = document.createElement('link');
          linkElement.setAttribute('rel', rel);
          linkElement.setAttribute('hreflang', hreflang);
          document.head.appendChild(linkElement);
        }
        
        linkElement.setAttribute('href', href);
      };
      
      createOrUpdateLinkElement('alternate', 'sk', window.location.href);
      createOrUpdateLinkElement('alternate', 'en', window.location.href.replace('/sk/', '/en/'));
      createOrUpdateLinkElement('alternate', 'hu', window.location.href.replace('/sk/', '/hu/'));
      createOrUpdateLinkElement('alternate', 'x-default', window.location.href.replace('/sk/', '/en/'));
    }
    
    // Ensure Quicksand font is loaded
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700"]')) {
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap';
      document.head.appendChild(fontLink);
    }
    
    // Add structured data for classes if available
    if (uniqueClasses.length > 0 && selectedInstructor) {
      const schemaData = generateSchemaOrgData();
      
      if (schemaData) {
        // Add or update JSON-LD script
        let scriptTag = document.getElementById('classes-schema');
        if (!scriptTag) {
          scriptTag = document.createElement('script');
          scriptTag.id = 'classes-schema';
          scriptTag.type = 'application/ld+json';
          document.head.appendChild(scriptTag);
        }
        
        scriptTag.textContent = JSON.stringify(schemaData);
      }
    }
    
    return () => {
      // Optional cleanup if component unmounts
    };
  }, [isSlovak, uniqueClasses, selectedInstructor, pageTitleSk, pageDescriptionSk]);
  
  return (
    <div className="yoga-classes-section-container" lang={i18n.language}>
      <div className="yoga-classes-section-header">
        <h1 style={{ color: theme?.neutral2 || '#3a3a3a' }}>{t('our_classes')}</h1>
        <div className="yoga-intro-container">
          <p className="yoga-intro-text" style={{ color: theme?.neutral2 || '#3a3a3a' }}>
            {t('classes_intro_text')}
          </p>
          <div className="yoga-intro-decoration" style={{ color: theme?.primary || '#8e44ad' }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 0C19.9778 9.48891 26.5111 16.0222 36 18C26.5111 19.9778 19.9778 26.5111 18 36C16.0222 26.5111 9.48891 19.9778 0 18C9.48891 16.0222 16.0222 9.48891 18 0Z" fill="currentColor" opacity="0.5"/>
            </svg>
          </div>
        </div>
      </div>
      
      {error && <div className="yoga-error-message">{error}</div>}

      <div className="yoga-classes-content">
        <div className="yoga-instructors-list-container">
          <h2 style={{ color: theme?.neutral2 || '#3a3a3a', borderBottom: `2px solid ${theme?.light || '#f0e6f7'}` }}>{t('our_instructors')}</h2>
          <div className="yoga-instructors-list">
            {instructors.map(instructor => (
              <div 
                key={instructor.id} 
                className={`yoga-instructor-card ${selectedInstructor?.id === instructor.id ? 'active' : ''}`}
                onClick={() => handleInstructorClick(instructor)}
                style={selectedInstructor?.id === instructor.id ? 
                  { borderColor: theme?.primary || '#8e44ad', backgroundColor: theme?.light || '#f0e6f7' } : {}}
              >
                <div className="yoga-instructor-image">
                  <img 
                    src={instructor.image_url || 'https://via.placeholder.com/150/f0e6f7/8e44ad?text=Instructor'} 
                    alt={instructor.name} 
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/150/f0e6f7/8e44ad?text=Instructor';
                    }}
                  />
                </div>
                <div className="yoga-instructor-info">
                  <h3 style={{ color: theme?.neutral2 || '#333' }}>{instructor.name}</h3>
                  <p style={{ color: theme?.neutral1 || '#666' }}>{instructor.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="yoga-classes-list-container" ref={classesListRef}>
          {loading ? (
            <div className="yoga-loading-spinner">{t('loading_classes')}</div>
          ) : selectedInstructor ? (
            <>
              <h2 style={{
                fontSize: '1.8rem',
                color: theme?.neutral2 || '#3a3a3a',
                borderBottom: `2px solid ${theme?.light || '#f0e6f7'}`,
                paddingBottom: '0.75rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span>{t('classes_by')}:</span>
                <span style={{ 
                  marginLeft: '80px', 
                  marginRight: '0px',
                  color: theme?.primary || '#8e44ad',   
                  fontWeight: '400' 
                }}>
                  {selectedInstructor.name}
                </span>
              </h2>
              
              {uniqueClasses.length > 0 ? (
                <div className="cs-classes-container">
                  {uniqueClasses.map((yogaClass, index) => {
                    // Normalize the class data to ensure we have consistent field names
                    const classTitle = yogaClass.title || yogaClass.name || yogaClass.class_name || t('unnamed_class');
                    const classDuration = yogaClass.duration || 60;
                    const classCapacity = yogaClass.max_capacity || 20;
                    const classPrice = yogaClass.price !== undefined ? yogaClass.price : 0;
                    const classDifficulty = yogaClass.difficulty || 2;
                    const classDate = yogaClass.date || '';
                    const classTime = yogaClass.start_time || '';
                    
                    // Get image URL for the class
                    const imageUrl = classImages[yogaClass.id] && classImages[yogaClass.id][0] 
                      ? classImages[yogaClass.id][0]
                      : yogaClass.processedImages && yogaClass.processedImages.length > 0 
                        ? yogaClass.processedImages[0] 
                        : 'https://via.placeholder.com/600x400/f0e6f7/8e44ad?text=' + encodeURIComponent(classTitle);
                    
                    return (
                      <div key={`${classTitle}-${index}`} className="cs-class-card" itemScope itemType="https://schema.org/Course">
                        <meta itemProp="name" content={classTitle} />
                        <meta itemProp="description" content={yogaClass.description || t('no_description')} />
                        <meta itemProp="provider" content="TiDeaSolis Yoga tdio" />
                        <meta itemProp="instructor" content={yogaClass.instructor_name || yogaClass.instructor || selectedInstructor.name} />
                        <meta itemProp="timeRequired" content={`PT${classDuration}M`} />
                        
                        <div className="cs-class-image">
                          <img 
                            src={imageUrl}
                            alt={classTitle}
                            itemProp="image"
                            onError={(e) => {
                              // console.error('Failed to load image:', e.target.src);
                              e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f0e6f7'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='24' fill='%238e44ad' text-anchor='middle' dominant-baseline='middle'%3E${encodeURIComponent(classTitle)}%3C/text%3E%3C/svg%3E`;
                              e.target.onerror = null;
                            }}
                          />
                        </div>
                        
                        <div className="cs-class-content">
                          <h4 className="cs-class-title">
                            {classTitle}
                            <span className={`cs-difficulty-tag cs-difficulty-${
  classDifficulty === 'various' 
    ? 'various' 
    : classDifficulty === 1 
      ? 'beginner' 
      : classDifficulty === 2 
        ? 'intermediate' 
        : 'advanced'
}`}>
                              {getDifficultyText(classDifficulty)}
                            </span>
                          </h4>
                          
                          <div className="cs-class-info">
                            <div className="cs-class-duration">
                              <span className="cs-info-label">{t('duration')}</span> <span itemProp="timeRequired">{classDuration} {t('minutes')}</span>
                            </div>
                            <div className="cs-class-capacity">
                              <span className="cs-info-label">{t('capacity')}:</span> 
                              <span className="cs-capacity-values">{classCapacity}</span>
                            </div>
                            <div className="cs-class-price">
                              <span className="cs-info-label">{t('price')}:</span> 
                              <span className="cs-price-value" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                                <meta itemProp="priceCurrency" content="EUR" />
                                <span itemProp="price">{classPrice}</span> &euro;
                              </span>
                            </div>
                          </div>
                          
                          {yogaClass.description && (
                            <div className="cs-class-description" itemProp="description">
                              {yogaClass.description.length > 100 ? 
                                `${yogaClass.description.substring(0, 100)}...` : 
                                yogaClass.description}
                            </div>
                          )}
                          
                          <div className="cs-instructor-name">
                            <span className="cs-info-label">{t('instructor')}</span> 
                            <span itemProp="instructor" itemScope itemType="https://schema.org/Person">
                              <span itemProp="name">{yogaClass.instructor_name || yogaClass.instructor || selectedInstructor.name}</span>
                            </span>
                          </div>
                          
                          <button className="cs-show-details-button" onClick={() => {
                            const formattedClass = {
                              ...yogaClass,
                              id: yogaClass.id,
                              title: classTitle,
                              description: yogaClass.description || '',
                              instructor: yogaClass.instructor_name || yogaClass.instructor || selectedInstructor.name,
                              instructor_image: yogaClass.instructor_image || selectedInstructor.image_url,
                              difficulty: classDifficulty,
                              max_capacity: classCapacity,
                              price: classPrice,
                              benefits: yogaClass.benefits || [],
                              images: classImages[yogaClass.id] || yogaClass.processedImages || []
                            };
                            // console.log('Sending class to details modal:', formattedClass);
                            setSelectedClass(formattedClass);
                            setShowClassDetails(true);
                          }}>
                            {t('show_details')} <span className="cs-arrow">&raquo;</span>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  padding: '3rem 2rem',
                  textAlign: 'center',
                  color: theme?.neutral1 || '#666',
                  backgroundColor: theme?.light || '#f9f9f9',
                  borderRadius: '8px',
                  minHeight: '250px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: theme?.secondary || '#f0e6f7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    fontSize: '2rem',
                    color: theme?.primary || '#8e44ad'
                  }}>
                    &quest;
                  </div>
                  <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>{t('no_classes_for_instructor')}</p>
                  <p style={{ margin: '0', fontSize: '0.9rem', maxWidth: '350px' }}>{t('check_back_later')}</p>
                </div>
              )}
            </>
          ) : (
            <div style={{
              padding: '3rem 2rem',
              textAlign: 'center',
              color: theme?.neutral1 || '#666',
              backgroundColor: theme?.light || '#f9f9f9',
              borderRadius: '8px',
              minHeight: '250px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.1rem',
              boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: theme?.secondary || '#f0e6f7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                fontSize: '2rem',
                color: theme?.primary || '#8e44ad'
              }}>
                &rarr;
              </div>
              <p style={{ margin: '0' }}>{t('select_instructor')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Class Details Modal */}
      {showClassDetails && selectedClass && (
        <ClassDetails 
          yogaClass={selectedClass}
          onClose={handleCloseClassDetails}
        />
      )}
    </div>
  );
};

export default ClassesSection; 